import { useEffect, useRef } from "react"
import { Platform, Pressable, TextInput as RNTextInput, View } from "react-native"
import MIcon from "@react-native-vector-icons/material-design-icons"
import { Controller, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { manualTransactionInput } from "@white-labels/input/src/m5azen"
import { ModalWrapper, Text, TextInput } from "@src/components/core"
import { ar } from "@src/i18n/ar"
import { trpc } from "@src/utils/trpc"
import { PriceInput } from "./price-input"

interface ManualBalanceTransactionModalProps {
  onClose: () => void
}

export function ManualBalanceTransactionModal({ onClose }: ManualBalanceTransactionModalProps) {
  const utils = trpc.useUtils()
  const manualTransaction = trpc.balance.manualTransaction.useMutation()
  const amountRef = useRef<RNTextInput | null>(null)

  const { control, handleSubmit, formState } = useForm({
    resolver: zodResolver(manualTransactionInput),
    defaultValues: {
      type: "deposit" as const,
      balanceType: "cash" as const,
    },
  })

  const { isValid, isDirty } = formState
  const loading = manualTransaction.isPending
  const disabled = !isValid || !isDirty || loading

  const onSubmit = handleSubmit((data) => {
    manualTransaction.mutate(data, {
      onSuccess: () => {
        utils.balance.get.invalidate()
        utils.balance.track.invalidate({ type: data.balanceType })
        onClose()
      },
    })
  })

  useEffect(() => {
    requestAnimationFrame(() => {
      if (amountRef.current) {
        amountRef.current.focus()
      }
    })
  }, [])

  // Close on Escape (web)
  useEffect(() => {
    if (Platform.OS === "web") {
      const handler = (e: KeyboardEvent) => {
        if (e.key === "Escape") onClose()
      }
      if (typeof window !== "undefined") {
        window.addEventListener("keydown", handler)
        return () => window.removeEventListener("keydown", handler)
      }
    }
  }, [onClose])

  return (
    <ModalWrapper onClose={onClose}>
      <View className="flex-row items-center justify-between px-2 border-b border-gray-100">
        <Text className="text-lg font-extrabold text-gray-900 tracking-tight">{ar.manualBalanceTransaction}</Text>
        <Pressable onPress={onClose} className="p-3">
          <MIcon name="close" size={22} color="#6B7280" />
        </Pressable>
      </View>

      <Controller
        control={control}
        name="type"
        render={({ field: { value, onChange } }) => (
          <View className="flex-row gap-2 bg-gray-50 rounded p-2 border border-gray-200">
            <Pressable
              className={`flex-1 h-8 rounded items-center justify-center active:opacity-80 ${value === "deposit" ? "bg-green-600" : "bg-white"} border ${value === "deposit" ? "border-green-600" : "border-gray-200"}`}
              onPress={() => onChange("deposit")}
            >
              <Text className={`font-semibold text-sm ${value === "deposit" ? "text-white" : "text-gray-700"}`}>{ar.deposite}</Text>
            </Pressable>
            <Pressable
              className={`flex-1 h-8 rounded items-center justify-center active:opacity-80 ${value === "withdraw" ? "bg-red-600" : "bg-white"} border ${value === "withdraw" ? "border-red-600" : "border-gray-200"}`}
              onPress={() => onChange("withdraw")}
            >
              <Text className={`font-semibold text-sm ${value === "withdraw" ? "text-white" : "text-gray-700"}`}>{ar.withdraw}</Text>
            </Pressable>
          </View>
        )}
      />
      <Controller
        control={control}
        name="balanceType"
        render={({ field: { value, onChange } }) => (
          <View className="flex-row gap-2 bg-gray-50 rounded p-2 border border-gray-200">
            <Pressable
              className={`flex-1 h-8 rounded items-center justify-center active:opacity-80 border ${value === "cash" ? "bg-green-50 border-green-500" : "bg-white border-gray-200"}`}
              onPress={() => onChange("cash")}
            >
              <Text className={`font-semibold text-sm ${value === "cash" ? "text-green-700" : "text-gray-700"}`}>{ar.cash}</Text>
            </Pressable>
            <Pressable
              className={`flex-1 h-8 rounded items-center justify-center active:opacity-80 border ${value === "visa" ? "bg-blue-50 border-blue-500" : "bg-white border-gray-200"}`}
              onPress={() => onChange("visa")}
            >
              <Text className={`font-semibold text-sm ${value === "visa" ? "text-blue-700" : "text-gray-700"}`}>{ar.visa}</Text>
            </Pressable>
            <Pressable
              className={`flex-1 h-8 rounded items-center justify-center active:opacity-80 border ${value === "wallet" ? "bg-orange-50 border-orange-500" : "bg-white border-gray-200"}`}
              onPress={() => onChange("wallet")}
            >
              <Text className={`font-semibold text-sm ${value === "wallet" ? "text-orange-700" : "text-gray-700"}`}>{ar.wallet}</Text>
            </Pressable>
          </View>
        )}
      />
      <Text className="text-base font-bold text-gray-900 mb-2 text-left">{ar.amount}</Text>
      <Controller
        control={control}
        name="amount"
        render={({ field: { onChange, value } }) => (
          <View className="relative">
            <PriceInput
              ref={amountRef}
              value={value}
              onValueChange={onChange}
              selectTextOnFocus
              textAlign="right"
              className="h-[46] px-2 border bg-white border-gray-300 focus:border-blue-600 rounded text-right"
            />
            <View className="absolute left-5 top-1/2 -translate-y-1/2">
              <Text className="text-gray-500 font-medium text-sm">{ar.egp}</Text>
            </View>
          </View>
        )}
      />
      <Text className="text-base font-bold text-gray-900 mb-2 text-left">{ar.notes}</Text>
      <Controller
        control={control}
        name="notes"
        render={({ field: { onChange, value } }) => (
          <TextInput placeholder={ar.addOptionalNote} placeholderTextColor="#9CA3AF" textAlignVertical="top" value={value} onChangeText={onChange} />
        )}
      />
      <View className="h-px via-gray-200 to-transparent mb-3" />
      <Pressable
        className={`h-8 rounded mb-4 items-center justify-center active:opacity-90 disabled:opacity-50 shadow-sm ${disabled ? "bg-gray-400" : "bg-blue-600"}`}
        onPress={onSubmit}
        disabled={disabled}
      >
        <Text className="text-white font-bold text-lg tracking-wide">{loading ? ar.loading : ar.confirm}</Text>
      </Pressable>
    </ModalWrapper>
  )
}
